import type { ReactNode } from 'react';
import type { Id, TicketDetail } from '../../api/types';
import { formatCalendarDate, formatPoints, versionNames } from '../../lib/format';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import { Avatar } from '../ui/Avatar';

/**
 * The fields, in the order Or's own detail panel shows them (screenshot 3).
 *
 * 🔴 THREE OF THEM ARE IDs, NOT NAMES — and they are labelled as such rather than dressed up.
 * `GET /tickets/:id` returns `sprint_id`, `version_id` and `parent_id`; there is no sprint or version
 * endpoint yet, so their NAMES are not obtainable. ⭐ Printing `#4` and saying "name unavailable" is
 * honest; printing nothing would read as "this ticket has no sprint", which is a different fact.
 */
export function DetailFields({
  ticket,
  onOpenTicket,
}: {
  ticket: TicketDetail;
  onOpenTicket: (id: Id) => void;
}) {
  const vocab = useVocabulary();
  const points = formatPoints(ticket.story_points);
  const impact = vocab.impact(ticket.impact);

  return (
    <dl className="grid grid-cols-[minmax(6.5rem,auto)_minmax(0,1fr)] items-baseline gap-x-3 gap-y-2.5 text-sm">
      <Field label="Assignee">
        <Person name={ticket.assignee} />
      </Field>
      <Field label="Reporter">
        <Person name={ticket.reporter} />
      </Field>
      <Field label="Priority">{vocab.priority(ticket.priority)?.label ?? '—'}</Field>
      <Field label="Impact">{impact ? <Tag>{impact.label}</Tag> : '—'}</Field>
      <Field label="Story Points">{points ?? '—'}</Field>
      <Field label="Type">{vocab.type(ticket.type)?.label ?? ticket.type}</Field>
      <Field label="Resolution">{ticket.resolution ?? '—'}</Field>
      <Field label="Sprint">
        <IdOnly id={ticket.sprint_id} noun="sprint" />
      </Field>
      <Field label="Version">
        <IdOnly id={ticket.version_id} noun="version" />
      </Field>
      <Field label="Module">
        <TagList values={ticket.modules} />
      </Field>
      <Field label="Fix Version">
        {/* ⚠️ Both shapes — the API sends names, this app was built against `{id, name}`. */}
        <TagList values={ticket.fix_versions && versionNames(ticket.fix_versions)} />
      </Field>
      {/* ⛔ CALENDAR dates — `ticket.start_date`/`end_date` are Postgres DATE. */}
      <Field label="Start date">{formatCalendarDate(ticket.start_date)}</Field>
      <Field label="End date">{formatCalendarDate(ticket.end_date)}</Field>
      <Field label="Parent">
        {ticket.parent_id ? (
          // ⭐ The parent's TITLE is not in the response, but its id is enough to open it — which is
          // what someone actually wants from this row.
          <button
            type="button"
            onClick={() => onOpenTicket(ticket.parent_id as Id)}
            className="text-[color:var(--color-accent)] hover:underline"
          >
            Open parent #{ticket.parent_id}
          </button>
        ) : (
          'None'
        )}
      </Field>
      <Field label="GitLab issue">
        {ticket.gitlab_issue_iid ? (
          /* ⭐ An IDENTIFIER, not a state. db/001 refuses to store MR or pipeline status, so nothing
             here could be a drifted copy of GitLab's own view. */
          <span className="font-mono text-xs">#{ticket.gitlab_issue_iid}</span>
        ) : (
          <span className="text-[color:var(--color-ink-muted)]">
            not created yet — created on entering DEV To Do
          </span>
        )}
      </Field>
    </dl>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-[color:var(--color-ink-muted)]">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  );
}

function IdOnly({ id, noun }: { id: Id | null; noun: string }) {
  if (!id) return <span className="text-[color:var(--color-ink-muted)]">None</span>;
  return (
    <span title={`The API returns only the ${noun} id; there is no ${noun} endpoint yet`}>
      <span className="font-mono text-xs">#{id}</span>
      <span className="ml-1.5 text-xs text-[color:var(--color-ink-subtle)]">(name unavailable)</span>
    </span>
  );
}

function Person({ name }: { name: string | null | undefined }) {
  if (!name) return <span className="text-[color:var(--color-ink-muted)]">Unassigned</span>;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <Avatar name={name} size={20} />
      <span className="truncate">{name}</span>
    </span>
  );
}

function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-block rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface-sunken)] px-1.5 py-0.5 text-xs">
      {children}
    </span>
  );
}

/** `undefined` = the API did not return the field; `[]` = the ticket genuinely has none. */
function TagList({ values }: { values?: string[] }) {
  if (values === undefined) {
    return <span className="text-xs text-[color:var(--color-ink-muted)]">not returned by the API</span>;
  }
  if (values.length === 0) return <span className="text-[color:var(--color-ink-muted)]">None</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {values.map((value) => (
        <Tag key={value}>{value}</Tag>
      ))}
    </span>
  );
}
